import React, { useState } from "react";
import { emitCustomEvent } from "react-custom-events";
import { Modal } from "antd";
import Input from "../form/Input";
import Company from "../../api/Company";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";

const AddCompanyModal = ({ company = null, visible, title, onClose }) => {
  const { t } = useTranslation();
  const [name, setName] = useState(company?.name);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState(null);
  const [isEnterprise, setIsEnterprise] = useState(
    company?.is_enterprise === 1 ? true : false
  );
  const addCompany = () => {
    setLoading(true);

    const request = company
      ? Company.update(company.id, {
          name: name,
          is_enterprise: isEnterprise ? 1 : 0,
        })
      : Company.add({ name: name, is_enterprise: isEnterprise ? 1 : 0 });

    request
      .then(() => {
        toast.success(
          company ? t("companies.company-updated") : t("companies.company-added")
        );
        emitCustomEvent("companyAdded");
        onClose();
      })
      .catch((error) => {
        if (error.response && error.response.status === 428) {
          toast.error(
            "Action discarded! Enterprise should have more than 1 location."
          );
        } else if (error.response && error.response.status === 422) {
          // Validation error
          setValidationErrors(error.response.data.errors);
        } else {
          if (error?.response?.status !== 403) {
            toast.error(t("common.something-went-wrong-please-try-again"));
          }
        }
      })
      .then(() => setLoading(false));
  };

  return (
    <Modal
      title={null}
      visible={visible}
      onCancel={onClose}
      closable={false}
      footer={null}
    >
      <div className="w-full">
        {/* Modal Header */}
        <div className="relative w-full bg-primary-500 px-6 py-4">
          <h2 className="text-xl text-white font-medium">{title}</h2>
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-white/80 hover:text-white transition-colors"
            onClick={onClose}
          >
            <i className="far fa-times text-xl" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          <div className="space-y-6">
            {/* Company Name Input */}
            <div>
              <Input
                name="name"
                label={t("common.name")}
                type="text"
                value={name}
                placeholder="Enter company name"
                error={
                  validationErrors && validationErrors.name
                    ? validationErrors.name[0]
                    : null
                }
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {/* Enterprise Toggle */}
            <div className="flex items-center space-x-3 bg-gray-50">
              <div className="flex-1">
                <label className="font-medium text-gray-900">
                  Enterprise Company
                </label>
                <p className="text-sm text-gray-500">
                  Enable this if this is an enterprise level company
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isEnterprise}
                  onChange={() => setIsEnterprise(!isEnterprise)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex flex-col-reverse sm:flex-row gap-3">
            <button
              className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg border-2 border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              onClick={() => emitCustomEvent("closeModal")}
            >
              {t("common.cancel")}
            </button>
            <button
              className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-primary-500 text-white hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-300 disabled:cursor-not-allowed inline-flex items-center justify-center"
              onClick={(e) => {
                e.preventDefault();
                addCompany();
              }}
              disabled={loading}
            >
              {loading ? (
                <i className="fad fa-circle-notch fa-spin mr-2" />
              ) : null}
              {t("common.save")}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AddCompanyModal;
